import React, { useEffect, useMemo, useState } from "react";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../../components/Models/admin-modal-theme";
import { closeCreateAnnouncement } from "../../../../modules/admin-modules/models/Actions";
import {
  createAnnouncementRequest,
  editAnnouncementRequest,
} from "../../../../modules/admin-modules/announcements/Actions";
import { API } from "../../../../constants";

const AddAnnouncement = ({ isAdd, data, setSelectedData }) => {
  const openAnnouncement = useSelector((state) => state.model.isOpen39);
  const isLoading = useSelector((state) => state.announcements.isLoading);
  const [fileLabel, setFileLabel] = useState(null);
  const baseUrl = API || "https://api.edutopiaonline.com/";
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { active: 1, title: "", content: "" },
    shouldUnregister: true,
  });

  const imageFile = watch("image");
  const isActive = watch("active");

  useEffect(() => {
    if (data?.id != null && !isAdd) {
      reset({
        title: data.title ?? "",
        content: data.content ?? "",
        active: data.active === 0 ? 0 : 1,
      });
      setValue("active", data.active === 0 ? 0 : 1);
      setFileLabel(null);
    } else {
      reset({ title: "", content: "", active: 1 });
      setValue("active", 1);
      setFileLabel(null);
    }
  }, [data, isAdd, reset, setValue]);

  const header = useMemo(
    () => (
      <Box>
        <Heading
          fontSize="32px"
          fontWeight="800"
          color={brandPalette.primary}
          fontFamily="'Amiri','Tajawal',sans-serif"
        >
          {isAdd ? "Add announcement" : "Edit announcement"}
        </Heading>
        <Text
          fontSize="xs"
          mt={1}
          color="var(--text-muted)"
          textTransform="uppercase"
          letterSpacing="0.14em"
          fontWeight="700"
        >
          Title, content, image, and visibility
        </Text>
      </Box>
    ),
    [isAdd]
  );

  const onSubmit = async (formData) => {
    const formDataObj = new FormData();

    for (const key in formData) {
      if (key !== "image") {
        formDataObj.append(key, formData[key]);
      }
    }

    if (imageFile && imageFile instanceof File) {
      formDataObj.append("image", imageFile);
    }

    const activeValue = formData.active === "1" || formData.active === 1 ? 1 : 0;
    formDataObj.set("active", activeValue);

    const action = () => {
      dispatch(closeCreateAnnouncement());
      setSelectedData({});
      reset({ title: "", content: "", active: 1 });
      setFileLabel(null);
    };

    if (isAdd) {
      await dispatch(createAnnouncementRequest(formDataObj, action));
    } else {
      formDataObj.append("_method", "PUT");
      await dispatch(
        editAnnouncementRequest({
          id: data?.id,
          formData: formDataObj,
          action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateAnnouncement());
    setSelectedData({});
    reset({ title: "", content: "", active: 1 });
    setFileLabel(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please choose a JPEG, JPG, or PNG image.");
      return;
    }

    setValue("image", file);
    setFileLabel(file.name);
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openAnnouncement}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <FormControl isInvalid={Boolean(errors?.title)}>
            <Stack spacing={2}>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
                textAlign="left"
              >
                Title
              </FormLabel>
              <Input
                {...register("title", { required: "Title is required" })}
                placeholder="Enter announcement title"
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                border="1px solid"
                borderColor={errors?.title ? "red.400" : "var(--border-subtle)"}
                borderRadius="lg"
                _focus={{
                  borderColor: brandPalette.primary,
                  boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.22)",
                }}
                _placeholder={{ color: "var(--text-muted)" }}
              />
              {errors?.title && (
                <Text color="red.500" fontSize="sm">
                  {errors.title.message}
                </Text>
              )}
            </Stack>
          </FormControl>

          <FormControl isInvalid={Boolean(errors?.content)}>
            <Stack spacing={2}>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
                textAlign="left"
              >
                Content
              </FormLabel>
              <Textarea
                {...register("content", { required: "Content is required" })}
                placeholder="Write the announcement content"
                rows={6}
                bg="var(--surface-muted)"
                color="var(--text-primary)"
                border="1px solid"
                borderColor={errors?.content ? "red.400" : "var(--border-subtle)"}
                borderRadius="lg"
                _focus={{
                  borderColor: brandPalette.primary,
                  boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.22)",
                }}
                _placeholder={{ color: "var(--text-muted)" }}
              />
              {errors?.content && (
                <Text color="red.500" fontSize="sm">
                  {errors.content.message}
                </Text>
              )}
            </Stack>
          </FormControl>

          <FormControl>
            <Stack spacing={2}>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
                textAlign="left"
              >
                Image
              </FormLabel>
              <HStack align="flex-start" spacing={4} flexWrap="wrap">
                <Button
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="lg"
                  position="relative"
                  h="42px"
                  fontWeight="700"
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  <Text>{fileLabel || "Choose file to upload"}</Text>
                  <Input
                    onChange={handleImageChange}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    opacity="0"
                    position="absolute"
                    w="100%"
                    h="100%"
                    left={0}
                    top={0}
                    cursor="pointer"
                  />
                </Button>
                {data?.image && !imageFile && (
                  <Image
                    src={`${baseUrl}${data.image}`}
                    alt={data.title}
                    maxW="120px"
                    maxH="120px"
                    objectFit="cover"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="var(--border-subtle)"
                  />
                )}
              </HStack>
              <Text fontSize="xs" color="var(--text-muted)">
                Optional. JPEG, JPG, or PNG.
              </Text>
            </Stack>
          </FormControl>

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color="var(--text-primary)"
                textAlign="left"
              >
                Active
              </FormLabel>
              <Text fontSize="xs" color="var(--text-muted)" mt={1}>
                Only active announcements are shown to students.
              </Text>
            </Box>
            <Switch
              isChecked={isActive === 1 || isActive === "1"}
              colorScheme="green"
              onChange={(e) => setValue("active", e.target.checked ? 1 : 0)}
            />
          </FormControl>
          <input type="hidden" {...register("active")} />

          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddAnnouncement;
